import {
  HeadContent,
  Scripts,
  createRootRoute,
  redirect,
  useRouterState,
} from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  PaletteMode,
} from '@mui/material'
import { useState, useMemo, createContext, useContext } from 'react'
import { AppDrawer } from '../components/Drawer'
import { StealthModeProvider } from '../contexts/StealthModeContext'
import { verifyToken } from '../data/auth/verifyToken'
import { CurrentUserProvider, useCurrentUser } from '../pages/Notes/CurrentUserContext'
import { UserPickerModal } from '../pages/Notes/UserPickerModal'

const ColorModeContext = createContext({ toggleColorMode: () => {} })

export const useColorMode = () => useContext(ColorModeContext)

const queryClient = new QueryClient()

// Shows the user picker modal when the app is ready but no user is selected.
// Only rendered on authenticated pages (hideDrawer = false on login page).
function UserGate() {
  const { currentUser, isReady } = useCurrentUser()
  if (!isReady || currentUser) return null
  return <UserPickerModal />
}

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <h1 className="text-4xl font-bold mb-4">404 - Page Not Found</h1>
      <p className="text-lg mb-4">The page you're looking for doesn't exist.</p>
      <a href="/" className="text-blue-600 hover:underline">
        Go back home
      </a>
    </div>
  )
}

export const Route = createRootRoute({
  notFoundComponent: NotFound,
  beforeLoad: async ({ location }) => {
    // Skip auth check for login page
    if (location.pathname.startsWith('/login')) {
      return
    }

    // Skip on server-side rendering
    if (typeof window === 'undefined') {
      return
    }

    // Get token from localStorage
    const token = localStorage.getItem('auth-token')

    if (!token) {
      throw redirect({ to: '/login' })
    }

    // Verify token
    try {
      await verifyToken({ data: { token } })
    } catch (error) {
      // Invalid token, clear it and redirect
      localStorage.removeItem('auth-token')
      throw redirect({ to: '/login' })
    }
  },

  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Enablar - Recipe & Note Manager',
      },
      {
        name: 'description',
        content: 'Manage recipes and practice music notes',
      },
      {
        name: 'theme-color',
        content: '#667eea',
      },
      {
        name: 'apple-mobile-web-app-capable',
        content: 'yes',
      },
      {
        name: 'apple-mobile-web-app-status-bar-style',
        content: 'black-translucent',
      },
      {
        name: 'apple-mobile-web-app-title',
        content: 'Enablar',
      },
    ],
    links: [
      { rel: 'icon', href: '/favicon/favicon.ico', sizes: '48x48' },
      { rel: 'icon', href: '/favicon/favicon-16x16.png', type: 'image/png', sizes: '16x16' },
      { rel: 'icon', href: '/favicon/favicon-32x32.png', type: 'image/png', sizes: '32x32' },
      { rel: 'apple-touch-icon', href: '/favicon/apple-touch-icon.png' },
      { rel: 'manifest', href: '/favicon/site.webmanifest' },
      { rel: 'preload', href: '/bloody_hands.png', as: 'image' },
    ],
  }),

  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<PaletteMode>('light')
  const location = useRouterState({ select: (s) => s.location })

  const hideDrawer = location.pathname === '/login'

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
      },
    }),
    [],
  )

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#667eea',
          },
          secondary: {
            main: '#764ba2',
          },
          ...(mode === 'dark' && {
            background: {
              default: '#0a0a0a',
              paper: '#1a1a2e',
            },
          }),
        },
      }),
    [mode],
  )

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <StealthModeProvider>
            <ColorModeContext.Provider value={colorMode}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <CurrentUserProvider>
                  {!hideDrawer && <UserGate />}
                  {hideDrawer ? children : <AppDrawer>{children}</AppDrawer>}
                </CurrentUserProvider>
              </ThemeProvider>
            </ColorModeContext.Provider>
          </StealthModeProvider>
        </QueryClientProvider>
        <Scripts />
      </body>
    </html>
  )
}
